/**
 * Shareable opportunity-listing view state, encoded in the URL (ADV-1361).
 *
 * Per ADR-0038, state that should survive a reload, a back-button press, or a
 * copy-pasted link lives in the URL — that is the root fix for the old switcher
 * that navigated away and lost tab context. Per-user display chrome (which card
 * fields show, density, empty-column visibility) is NOT shareable and lives in
 * the persisted `opportunity-display-store.ts` instead.
 *
 * Param keys are kept short and human-legible (`?view=table&closed=1m`).
 * Defaults are omitted from the URL so the canonical path stays clean.
 */
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { LIST_PAGE_VIEWS } from "@/pages/v2/_shared/list-page-views";

export type OpportunityView = "board" | "table" | "analytics";
export type ClosedWindow = "1d" | "1w" | "2w" | "1m" | "3m" | "all";
type SubGroupBy = "territory" | "salesperson";

interface OpportunityOrdering {
  id: string;
  desc: boolean;
}

const OPPORTUNITY_VIEWS: readonly OpportunityView[] = [
  "board",
  "table",
  "analytics",
];
const CLOSED_WINDOWS: readonly ClosedWindow[] = [
  "1d",
  "1w",
  "2w",
  "1m",
  "3m",
  "all",
];
const SUB_GROUP_BYS: readonly SubGroupBy[] = ["territory", "salesperson"];

const DEFAULT_VIEW = LIST_PAGE_VIEWS.opportunities
  .defaultValue as OpportunityView;
const DEFAULT_CLOSED_WINDOW: ClosedWindow = "1m";

interface UseOpportunityViewParams {
  view: OpportunityView;
  setView: (next: OpportunityView) => void;
  // Faceted filters (salesperson `assigned_rep_id`, territory `territory_id`)
  // are URL params too, but they live in the dedicated `useOpportunityFilters`
  // hook (multi-select CSV, mirroring `use-jobs-filters`) — this hook owns only
  // display/view chrome.
  /** Closed-deals window. Control ships in S2; server-side WHERE lands in S7. */
  closedWindow: ClosedWindow;
  setClosedWindow: (next: ClosedWindow) => void;
  /** Secondary group-by (swimlanes). Param plumbing only until the v2 render. */
  subGroupBy?: SubGroupBy;
  setSubGroupBy: (next: SubGroupBy | undefined) => void;
  /** Within-column board ordering. */
  ordering?: OpportunityOrdering;
  setOrdering: (next: OpportunityOrdering | undefined) => void;
  /** Clears every display-related param (used by the popover Reset). */
  resetDisplayParams: () => void;
}

export function useOpportunityViewParams(): UseOpportunityViewParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const setParam = useCallback(
    (key: string, value: string | undefined) =>
      setSearchParams((prev) => {
        if (!value) prev.delete(key);
        else prev.set(key, value);
        return prev;
      }),
    [setSearchParams],
  );

  const rawView = searchParams.get("view");
  const view = (OPPORTUNITY_VIEWS as readonly string[]).includes(rawView ?? "")
    ? (rawView as OpportunityView)
    : DEFAULT_VIEW;

  const rawClosed = searchParams.get("closed");
  const closedWindow = (CLOSED_WINDOWS as readonly string[]).includes(
    rawClosed ?? "",
  )
    ? (rawClosed as ClosedWindow)
    : DEFAULT_CLOSED_WINDOW;

  const rawSub = searchParams.get("rows");
  const subGroupBy = (SUB_GROUP_BYS as readonly string[]).includes(rawSub ?? "")
    ? (rawSub as SubGroupBy)
    : undefined;

  // Ordering is serialized as `id:dir`, e.g. `lead_date:desc`.
  const rawSort = searchParams.get("sort");
  const ordering = useMemo<OpportunityOrdering | undefined>(() => {
    if (!rawSort) return undefined;
    const [id, dir] = rawSort.split(":");
    if (!id) return undefined;
    return { id, desc: dir !== "asc" };
  }, [rawSort]);

  return {
    view,
    setView: (next) =>
      setParam("view", next === DEFAULT_VIEW ? undefined : next),
    closedWindow,
    setClosedWindow: (next) =>
      setParam("closed", next === DEFAULT_CLOSED_WINDOW ? undefined : next),
    subGroupBy,
    setSubGroupBy: (next) => setParam("rows", next),
    ordering,
    setOrdering: (next) =>
      setParam(
        "sort",
        next ? `${next.id}:${next.desc ? "desc" : "asc"}` : undefined,
      ),
    // Display config only — faceted filters (assigned_rep_id) are a separate
    // concern owned by useOpportunityFilters and are not cleared by Reset.
    resetDisplayParams: () =>
      setSearchParams((prev) => {
        for (const key of ["closed", "rows", "sort"]) prev.delete(key);
        return prev;
      }),
  };
}
